import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface TraceRow {
  readonly traceabilityId: string;
  readonly traceCode: string;
  readonly requirementRef: string;
  readonly coverageStatus: string;
  readonly lastResult: string | null;
}

const COVERAGE = ['', 'NOT_COVERED', 'PARTIAL', 'COVERED'];

/**
 * M34 — Traceability Matrix Viewer (§31 / §16). Requirement→scenario→case→
 * execution→defect coverage matrix (T455). BR-01: every requirement must have
 * test coverage. Filterable by coverage status.
 */
export function TraceabilityMatrixViewer(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TraceRow[]>([]);
  const [coverageStatus, setCoverageStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (coverageStatus) params.set('coverageStatus', coverageStatus);
    const qs = params.toString();
    apiRequest<{ data: TraceRow[] }>(`/v1/qms/traceability${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, coverageStatus]);

  return (
    <main aria-label="traceability-matrix">
      <h1>{t('m34.trace.title')}</h1>
      <div className="filters">
        <label>
          {t('m34.trace.coverage')}
          <select aria-label="filter-coverage" value={coverageStatus} onChange={(e) => setCoverageStatus(e.target.value)}>
            {COVERAGE.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="trace-count">{t('m34.trace.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="trace-table"
            rows={rows}
            rowKey={(r) => r.traceabilityId}
            columns={[
              { key: 'traceCode', header: t('m34.common.code'), render: (r) => r.traceCode },
              { key: 'requirementRef', header: t('m34.trace.requirement'), render: (r) => r.requirementRef },
              { key: 'coverageStatus', header: t('m34.trace.coverage'), render: (r) => r.coverageStatus },
              { key: 'lastResult', header: t('m34.trace.lastResult'), render: (r) => r.lastResult ?? '—' },
            ]}
          />
        </>
      )}
    </main>
  );
}
